import type { HTMLAttributes } from "react";
import { cx } from "./cx";
import { VisuallyHidden } from "./VisuallyHidden";
import "./Spinner.css";

export type SpinnerSize = "sm" | "md" | "lg";

export interface SpinnerProps extends HTMLAttributes<HTMLSpanElement> {
  size?: SpinnerSize;
  /** Accessible text. Rendered visually when `showLabel` is set. Default "Loading". */
  label?: string;
  showLabel?: boolean;
  /** Hide from assistive tech (e.g. inside a button that already sets aria-busy). */
  decorative?: boolean;
  /** Use currentColor instead of the accent colour. */
  inheritColor?: boolean;
}

/** Indeterminate loading indicator with a text alternative. */
export function Spinner({
  size = "md",
  label = "Loading",
  showLabel = false,
  decorative = false,
  inheritColor = false,
  className,
  ...rest
}: SpinnerProps) {
  return (
    <span
      className={cx(
        "ui-spinner",
        `ui-spinner--${size}`,
        inheritColor && "ui-spinner--inherit",
        className,
      )}
      role={decorative ? undefined : "status"}
      aria-hidden={decorative || undefined}
      {...rest}
    >
      <span className="ui-spinner__ring" />
      {decorative ? null : showLabel ? <span>{label}</span> : <VisuallyHidden>{label}</VisuallyHidden>}
    </span>
  );
}
